import { useCallback } from "react";

import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { createProfile } from "../state/profile";
import { SectionTypes } from "./section-types.js";

export const publishUrl = (id) => {
  return `/write/${id}/pre-publish`;
};

export const editUrl = (id, type) => {
  return `/write/${type}/${id}`;
};

export const previewUrl = (id) => {
  return `/write/${id}/preview`;
};

export const getArticle = async (articleId) => {
  const res = await fetch(
    `/api/article/${articleId}?cachebreaker=${new Date().getTime()}`,
  );
  if (res.ok) {
    return await res.json();
  }
};

export const saveSection = async (section) => {
  const res = await fetch(`/api/section/${section.id ? section.id : ""}`, {
    method: section.id ? "PUT" : "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(section),
  });
  return res.ok ? await res.json() : seciton;
};

export const saveChunk = async (chunk) => {
  const res = await fetch(`/api/chunk/${chunk.id ? chunk.id : ""}`, {
    method: chunk.id ? "PUT" : "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(chunk),
  });
  if (res.ok) {
    const result = await res.json();
    return result;
  }
  return chunk;
};

export const deleteChunk = async (chunkId) => {
  const res = await fetch(`/api/chunk/${chunkId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
  });
  return res.ok === true;
};

export const deleteSection = async (sectionId) => {
  const res = await fetch(`/api/section/${sectionId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
  });
  return res.ok === true;
};

export const saveArticle = async (article) => {
  const res = await fetch(`/api/article/${article.id ? article.id : ""}`, {
    method: article.id ? "PUT" : "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(article),
  });
  return res.ok ? await res.json() : article;
};

const getUser = async (userId) => {
  const res = await fetch(`/api/user/${userId}`);
  return res.ok ? await res.json() : createProfile();
};

const getUserArticles = async (userId) => {
  const res = await fetch(`/api/user/${userId}/articles`);
  return res.ok ? await res.json() : [];
};

const updateUser = async ({ model, field }) => {
  const res = await fetch(`/api/user/${model.id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ [field]: model[field] }),
  });

  if (!res.ok) {
    throw new Error("Error saving user");
  }
  return await res.json();
};

const getSubscriptions = async () => {
  const res = await fetch(`/api/user/subscriptions`);
  return res.ok ? await res.json() : [];
};

const updateSubscription = async (subscription) => {
  const { article_id, enabled } = subscription;
  const res = await fetch(
    `/api/user/subscriptions/${article_id}/${enabled ? "false" : "true"}`,
    {
      method: "PUT",
    },
  );
  return res.ok;
};

const defaultSections = {
  article: ["overview", "content"],
  howto: [
    "overview",
    "tools",
    "parts",
    "instructions",
    "conclusion",
    "specs",
    "source",
  ],
};

const newDefaultSection = (section, index, type, article_id) => ({
  title: defaultSections[type].includes(section) ? section : "",
  category: section,
  order_index: index * 10,
  article_id: article_id,
});

const populateSectionPlaceholders = (article) => {
  const defaults = defaultSections[article.type];
  defaults.forEach((s, i) => {
    if (!article.sections.some((a) => a.category.toLowerCase() == s)) {
      article.sections.push(newDefaultSection(s, i, article.type, article.id));
    }
  });

  // Sort sections by order_index to maintain proper order
  article.sections.sort((a, b) => a.order_index - b.order_index);

  //   var j, len, ref1, t;
  //   const sectionsToAdd = [];
  //   ref1 = this.defaultSections[article.type];
  //
  //   for (j = 0, len = ref1.length; j < len; j++) {
  //
  // const cat = ref1[j].toLowerCase();
  //     const existingSection = article.sections.filter((s) => s.category == cat)
  //
  // if (!existingSection.length) {
  //       let i = defaultSections[article.type].indexOf(cat);
  //       if (i === -1) {
  //         cat = 'none';
  //         t = '';
  //         i = 0;
  //       } else {
  //         t = SectionTypes[cat].label;
  //       }
  //
  //       sectionsToAdd.push({
  //         title: t,
  //         category: cat,
  //         order_index: i * 10,
  //         article_id: article.id
  //       });
  //     }
  //   }
  //
  //   if (sectionsToAdd.length > 0) {
  //     article.sections.push(...sectionsToAdd);
  // //return this.updateStepIndexes();
  //   }
  //
};

export const useGetArticle = (articleId, type = "howto") =>
  useQuery({
    queryKey: ["article", articleId],
    queryFn: async () => {
      const result = await getArticle(articleId);
      populateSectionPlaceholders(result);
      return result;
    },
    enabled: !!articleId,
    initialData: {
      id: null,
      type,
      title: "",
      verb: "",
      subject: "",
      sections: [],
      spotlight: null,
      vehicle: "No Vehicle",
      editable: false,
      is_draft: true,
      user: "",
      locked: false,
    },
  });

export const setArticleCache = () => {
  const queryClient = useQueryClient();

  return useCallback((article, articleId) => {
    queryClient.setQueryData(["article", articleId], article);
  });
};

export const useSaveSection = () => {
  const queryClient = useQueryClient();
  // Mutations
  return useMutation({
    mutationFn: async (data) => {
      const res = await saveSection(data);
      return res;
    },
    onSuccess: (data, variables) => {
      const article = queryClient.getQueryData(["article", data.article_id]);
      const id = article.sections.findIndex((s) => s.category == data.category);
      if (id) {
        article.sections[id] = data;
        queryClient.setQueryData(["article", data.id], data);
      }
    },
  });
};

export const useSaveChunk = (articleId) => {
  const queryClient = useQueryClient();
  // Mutations
  return useMutation({
    mutationFn: async (data) => {
      const res = await saveChunk(data);
      return res;
    },
    onMutate: async (newChunk) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["article", articleId] });

      // Snapshot the previous value
      const previousArticle = queryClient.getQueryData(["article", articleId]);

      // Optimistically update to the new value
      queryClient.setQueryData(["article", articleId], (old) => {
        const article = { ...old };
        const idx = article.sections.findIndex(
          (s) => s.id == newChunk.section_id,
        );

        if (idx === -1) {
          return old;
        }

        // Find where to insert the chunk (after the specified chunk)
        const afterIdx = article.sections[idx].chunks.findIndex(
          (c) => c.id === newChunk.after_id,
        );

        if (afterIdx === -1) {
          article.sections[idx].chunks = [
            ...article.sections[idx].chunks,
            newChunk,
          ];
        } else {
          const newChunks = [...article.sections[idx].chunks];
          newChunks.splice(afterIdx + 1, 0, newChunk);
          article.sections[idx].chunks = newChunks;
        }

        article.sections[idx] = { ...article.sections[idx] };
        article.sections = [...article.sections];

        return article;
      });

      // Return a context object with the snapshotted value
      return { previousArticle };
    },
    onSuccess: ({ chunk, siblings }, variables, context) => {
      const article = queryClient.getQueryData(["article", articleId]);

      const idx = article.sections.findIndex((s) => s.id == chunk.section_id);
      if (idx === -1) {
        return;
      }

      // Find the placeholder chunk (no id) that was created optimistically
      const placeholderIdx = article.sections[idx].chunks.findIndex(
        (c) =>
          !c.id &&
          c.saveStatus === "Saving" &&
          c.title === variables.title &&
          c.after_id === variables.after_id,
      );

      if (placeholderIdx !== -1) {
        // Replace the placeholder with the real chunk
        article.sections[idx].chunks[placeholderIdx] = chunk;
        article.sections[idx].chunks = [...article.sections[idx].chunks];
        article.sections[idx] = { ...article.sections[idx] };
        article.sections = [...article.sections];

        queryClient.setQueryData(["article", articleId], { ...article });
      }
    },
    onError: (err, newChunk, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      queryClient.setQueryData(["article", articleId], context.previousArticle);
    },
  });
};

export const useUpdateChunk = (articleId) => {
  const queryClient = useQueryClient();
  // Mutations
  return useMutation({
    mutationFn: async (data) => {
      const res = await saveChunk(data);
      return res;
    },
    onMutate: async (updatedChunk) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["article", articleId] });

      // Snapshot the previous value
      const previousArticle = queryClient.getQueryData(["article", articleId]);

      // Optimistically update the chunk
      queryClient.setQueryData(["article", articleId], (old) => {
        const article = { ...old };
        const idx = article.sections.findIndex(
          (s) => s.id == updatedChunk.section_id,
        );

        if (idx === -1) {
          return old;
        }

        const chunkIdx = article.sections[idx].chunks.findIndex(
          (c) => c.id === updatedChunk.id,
        );

        if (chunkIdx === -1) {
          return old;
        }

        // Update the chunk
        article.sections[idx].chunks[chunkIdx] = {
          ...article.sections[idx].chunks[chunkIdx],
          ...updatedChunk,
          saveStatus: "Saving",
        };
        article.sections[idx].chunks = [...article.sections[idx].chunks];
        article.sections[idx] = { ...article.sections[idx] };
        article.sections = [...article.sections];

        return article;
      });

      // Return a context object with the snapshotted value
      return { previousArticle };
    },
    onSuccess: (chunk, variables, context) => {
      const article = queryClient.getQueryData(["article", articleId]);
      const idx = article.sections.findIndex((s) => s.id == chunk.section_id);

      if (idx === -1) {
        return;
      }

      const chunkIdx = article.sections[idx].chunks.findIndex(
        (c) => c.id === chunk.id,
      );

      if (chunkIdx === -1) {
        return;
      }

      // Update the chunk with the server response and set "Saved!!" status
      article.sections[idx].chunks[chunkIdx] = {
        ...chunk,
        saveStatus: "Saved!!",
      };
      article.sections[idx].chunks = [...article.sections[idx].chunks];
      article.sections[idx] = { ...article.sections[idx] };
      article.sections = [...article.sections];

      queryClient.setQueryData(["article", articleId], { ...article });

      // Clear the "Saved!!" status after 2 seconds
      setTimeout(() => {
        const currentArticle = queryClient.getQueryData(["article", articleId]);
        if (!currentArticle) return;

        const sectionIdx = currentArticle.sections.findIndex(
          (s) => s.id == chunk.section_id,
        );
        if (sectionIdx === -1) return;

        const currentChunkIdx = currentArticle.sections[
          sectionIdx
        ].chunks.findIndex((c) => c.id === chunk.id);
        if (currentChunkIdx === -1) return;

        // Only clear if it's still "Saved!!" (not changed to "Saving" again)
        if (
          currentArticle.sections[sectionIdx].chunks[currentChunkIdx]
            .saveStatus === "Saved!!"
        ) {
          currentArticle.sections[sectionIdx].chunks[currentChunkIdx] = {
            ...currentArticle.sections[sectionIdx].chunks[currentChunkIdx],
            saveStatus: undefined,
          };
          currentArticle.sections[sectionIdx].chunks = [
            ...currentArticle.sections[sectionIdx].chunks,
          ];
          currentArticle.sections[sectionIdx] = {
            ...currentArticle.sections[sectionIdx],
          };
          currentArticle.sections = [...currentArticle.sections];

          queryClient.setQueryData(["article", articleId], {
            ...currentArticle,
          });
        }
      }, 2000);
    },
    onError: (err, updatedChunk, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      queryClient.setQueryData(["article", articleId], context.previousArticle);
    },
  });
};

export const useDeleteChunk = (articleId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (chunkId) => {
      const result = await deleteChunk(chunkId);
      return result;
    },
    onMutate: async (chunkId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["article", articleId] });

      // Snapshot the previous value
      const previousArticle = queryClient.getQueryData(["article", articleId]);

      // Optimistically remove the chunk
      queryClient.setQueryData(["article", articleId], (old) => {
        if (!old || !old.sections) {
          return old;
        }

        const article = { ...old };
        article.sections = old.sections.map((section) => ({ ...section }));

        // Find the section containing this chunk
        for (let idx = 0; idx < article.sections.length; idx++) {
          if (!article.sections[idx].chunks) {
            continue;
          }

          const chunkIdx = article.sections[idx].chunks.findIndex(
            (c) => c.id === chunkId,
          );

          if (chunkIdx !== -1) {
            const newChunks = [...article.sections[idx].chunks];
            newChunks.splice(chunkIdx, 1);
            article.sections[idx].chunks = newChunks;
            break;
          }
        }

        return article;
      });

      return { previousArticle };
    },
    onSuccess: (result, chunkId) => {
      // Success handler
    },
    onError: (err, chunkId, context) => {
      queryClient.setQueryData(["article", articleId], context.previousArticle);
    },
  });
};

export const useMoveChunk = (articleId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ chunkId, beforeId, afterId }) => {
      const params = new URLSearchParams({ id: chunkId });
      if (beforeId) params.append("before_id", beforeId);
      if (afterId) params.append("after_id", afterId);

      const res = await fetch(`/api/chunk/move?${params.toString()}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        throw new Error("Failed to move chunk");
      }

      const orderedIds = await res.json();
      return orderedIds;
    },
    onMutate: async ({ chunkId, beforeId, afterId, sectionId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["article", articleId] });

      // Snapshot the previous value
      const previousArticle = queryClient.getQueryData(["article", articleId]);

      // Optimistically reorder chunks
      queryClient.setQueryData(["article", articleId], (old) => {
        if (!old || !old.sections) {
          return old;
        }

        const article = { ...old };
        const idx = article.sections.findIndex((s) => s.id === sectionId);

        if (idx === -1) {
          return old;
        }

        const section = { ...article.sections[idx] };
        const chunks = [...section.chunks];

        // Find the chunk to move
        const chunkIdx = chunks.findIndex((c) => c.id === chunkId);
        if (chunkIdx === -1) {
          return old;
        }

        // Remove the chunk from its current position
        const [movedChunk] = chunks.splice(chunkIdx, 1);

        // Find the target position
        let targetIdx;
        if (beforeId) {
          targetIdx = chunks.findIndex((c) => c.id === beforeId);
        } else if (afterId) {
          targetIdx = chunks.findIndex((c) => c.id === afterId);
          if (targetIdx !== -1) targetIdx += 1;
        }

        if (targetIdx === -1) {
          // Put it back
          chunks.splice(chunkIdx, 0, movedChunk);
          return old;
        }

        // Insert at the new position
        chunks.splice(targetIdx, 0, movedChunk);

        section.chunks = chunks;
        article.sections[idx] = section;
        article.sections = [...article.sections];

        return article;
      });

      return { previousArticle };
    },
    onSuccess: (orderedIds, variables) => {
      // Optionally refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ["article", articleId] });
    },
    onError: (err, variables, context) => {
      queryClient.setQueryData(["article", articleId], context.previousArticle);
    },
  });
};

export const useDeleteSection = (articleId) => {
  const queryClient = useQueryClient();
  // Mutations
  return useMutation({
    mutationFn: deleteSection,
    onSuccess: (_resp, sectionId) => {
      const article = queryClient.getQueryData(["article", articleId]);
      const i = article.sections.findIndex((s) => s.id === sectionId);
      if (i !== -1) {
        const removed = article.sections[i];
        // Replace with a placeholder section if it's a default section
        if (defaultSections[article.type].includes(removed.category)) {
          // Find the correct index from defaultSections to maintain proper order
          const correctIndex = defaultSections[article.type].indexOf(
            removed.category,
          );
          const newDefaultSecion = newDefaultSection(
            removed.category,
            correctIndex,
            article.type,
            article.id,
          );
          article.sections[i] = newDefaultSecion;
        } else {
          article.sections.splice(i, 1);
        }

        // Sort sections by order_index to maintain proper order
        article.sections.sort((a, b) => a.order_index - b.order_index);
        article.sections = [...article.sections];
        queryClient.setQueryData(["article", articleId], { ...article });
      }
    },
  });
};

export const useSaveArticle = () => {
  const queryClient = useQueryClient();
  // Mutations
  return useMutation({
    mutationFn: async (data) => {
      const res = await saveArticle(data);
      populateSectionPlaceholders(res);
      return res;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(["article", data.id], data);
    },
  });
};

export const useGetSubscriptions = (userId) =>
  useQuery({
    queryKey: ["subscriptions", userId],
    queryFn: () => getSubscriptions(),
    enabled: !!userId,
  });

export const useGetUser = (userId) =>
  useQuery({ queryKey: ["user", userId], queryFn: () => getUser(userId) });

export const useGetUserArticles = (userId) =>
  useQuery({
    queryKey: ["user-articles", userId],
    queryFn: () => getUserArticles(userId),
    enable: !!userId,
  });

export const useUpdateUser = (userId) => {
  const queryClient = useQueryClient();
  // Mutations
  return useMutation({
    mutationFn: updateUser,
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ["user", userId] });
    },
  });
};

export const useUpdateSubscription = (userId) => {
  const queryClient = useQueryClient();
  // Mutations
  return useMutation({
    mutationFn: updateSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscriptions", userId] });
    },
  });
};
